import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Clock } from "lucide-react";
import SEO from "../components/SEO";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import Editable from "../components/Editable";
import { useContentApi } from "../context/ContentContext";

const Shop = () => {
    const { c, loaded } = useContentApi();
    const comingSoon = loaded && c("shop.coming_soon", "false") === "true";

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("all");
    const [generalFund, setGeneralFund] = useState(null);

    useEffect(() => {
        if (comingSoon) return;             // skip the product fetch while the store is hidden
        setLoading(true);
        const params = category === "all" ? {} : { category };
        api.get("/products", { params })
            .then((r) => setProducts(r.data))
            .finally(() => setLoading(false));
    }, [category, comingSoon]);

    useEffect(() => {
        if (comingSoon) return;
        api.get("/campaigns/public/general-support").then((r) => setGeneralFund(r.data)).catch(() => {});
        api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    }, [comingSoon]);

    if (comingSoon) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-20" data-testid="shop-coming-soon">
                <SEO title="Spirit Shop — Coming Soon" description="The Reddog spirit shop is launching soon." />
                <div className="max-w-xl text-center">
                    <Clock className="w-12 h-12 mx-auto text-[#E53935] mb-4" strokeWidth={1.5} />
                    <Editable contentKey="shop.coming_soon_tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                        Almost there
                    </Editable>
                    <Editable contentKey="shop.coming_soon_title" as="h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mb-4">
                        Spirit Shop — coming soon
                    </Editable>
                    <Editable contentKey="shop.coming_soon_body" as="p" multiline className="text-base text-[#4A5568] leading-relaxed mb-8">
                        We&apos;re putting the finishing touches on our spirit shop. In the meantime, every dollar you give still goes straight to the kids and classrooms of Frenchtown.
                    </Editable>
                    <Link
                        to="/donate"
                        data-testid="shop-coming-soon-donate-link"
                        className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-3 text-sm font-medium"
                    >
                        Donate instead <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        );
    }

    const filterChips = [{ slug: "all", label: "All" }, ...categories.map((c) => ({ slug: c.slug, label: c.label }))];

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24" data-testid="shop-page">
            <SEO
                title="Shop · Spirit gear & sponsorships that fund the mission"
                description="Every purchase funds family aid and community programs. Browse Reddog Foundation spirit gear, accessories, and direct sponsorship items."
                path="/shop"
            />
            <div className="mb-6">
                <Editable contentKey="shop.tag" as="p" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">
                    The Shop
                </Editable>
                <Editable contentKey="shop.title" as="h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] font-medium tracking-tight">
                    Every item supports the mission.
                </Editable>
            </div>

            {/* General Support Fund explainer (top of shop, donor-facing) */}
            <div className="bg-gradient-to-br from-[#F2D8D5] to-[#FDFBF7] border border-[#E53935]/15 rounded-3xl p-6 sm:p-8 mb-10 max-w-3xl" data-testid="shop-merch-disclosure">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-2 inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Reddog General Support Fund
                </p>
                <Editable contentKey="shop.merch_explain" as="p" multiline className="text-sm sm:text-base text-[#4A5568] leading-relaxed">
                    Net proceeds from Reddog merchandise go into the Reddog General Support Fund, which helps support approved school programs, classroom needs, student meals, field trips, simple repairs, upgrades, and other local needs reviewed by the foundation.
                </Editable>
                {generalFund && (
                    <Link to="/ledger" data-testid="shop-gsf-link" className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#E53935] hover:underline">
                        See where every dollar goes — public ledger <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                )}
            </div>

            <div className="flex flex-wrap gap-3 mb-12" role="group" aria-label="Filter by category">
                {filterChips.map((c) => (
                    <button
                        key={c.slug}
                        onClick={() => setCategory(c.slug)}
                        data-testid={`shop-filter-${c.slug}-btn`}
                        aria-pressed={category === c.slug}
                        className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                            category === c.slug
                                ? "bg-[#E53935] text-white shadow-md"
                                : "bg-white text-[#4A5568] border border-black/10 hover:border-[#E53935]"
                        }`}
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-center text-[#4A5568] py-20" data-testid="shop-loading" role="status" aria-live="polite">Loading…</div>
            ) : products.length === 0 ? (
                <div className="text-center text-[#4A5568] py-20" data-testid="shop-empty">No products yet.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="shop-products-grid">
                    {products.map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            )}

            {/* How merchandise proceeds are used — detailed explanation */}
            <section className="mt-20 bg-white border border-black/5 rounded-3xl p-8 sm:p-12" data-testid="shop-how-used">
                <Editable contentKey="shop.how_used_title" as="h2" className="font-heading text-2xl sm:text-3xl text-[#1C252E] font-medium tracking-tight mb-4">
                    How merchandise proceeds are used
                </Editable>
                <Editable
                    contentKey="shop.how_used_body"
                    as="p"
                    multiline
                    className="text-[#4A5568] leading-relaxed max-w-3xl"
                >
                    Reddog merchandise is not tied to one single campaign unless clearly stated. Net proceeds go into the Reddog General Support Fund, which gives the foundation flexibility to support approved school programs, classroom needs, student meal assistance, field trips, simple repairs, upgrades, and other local needs. Supporters may choose a classroom or program to credit with their purchase, but final use of funds remains subject to foundation review, available balances, and approved charitable purposes.
                </Editable>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                    <div className="bg-[#F4F1EA] rounded-2xl p-5" data-testid="shop-fund-type-general">
                        <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-1">Reddog General Support Fund</p>
                        <p className="text-sm text-[#4A5568] leading-relaxed">Flexible — receives merch net proceeds, sponsorships, and undesignated gifts. Supports approved programs, classroom needs, meal accounts, field trips, simple repairs, and emergency needs.</p>
                    </div>
                    <div className="bg-[#F4F1EA] rounded-2xl p-5" data-testid="shop-fund-type-restricted">
                        <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-1">Restricted Campaigns</p>
                        <p className="text-sm text-[#4A5568] leading-relaxed">Earmarked for one specific purpose — e.g. Spring Field Trip Fund, Boiler Repair Fund, Playground Safety Fund. Money raised may only be spent on that approved purpose.</p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Shop;
